<template>
  <div>
    <!-- Search + Add button row -->
    <div class="row" style="margin: 0px 0px 20px 0px">
      <b-form-input
        type="search"
        class="col"
        autofocus
        :value="filter"
        @input="$emit('update:filter', $event)"
        placeholder="ค้นหาใบรับสินค้า จากรหัสใบรับสินค้า รหัสใบสั่งซื้ออ้างอิง หรือชื่อผู้ขาย"
      ></b-form-input>
      <b-button v-if="showAddButton" class="ml-3 add-gr-btn" @click="$emit('add-gr')">
        <i class="fas fa-plus"></i> เพิ่มรายการ
      </b-button>
    </div>

    <!-- GR table -->
    <b-table
      :id="tableId"
      hover
      small
      sort-icon-left
      head-variant="light"
      sort-by="createdAt"
      :items="items"
      :fields="grFields"
      :filter="filter"
      :filter-included-fields="['grCode', 'poRefCode', 'vendorName']"
      :sort-desc="true"
      :responsive="true"
    >
      <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>

      <template v-slot:cell(status)="row">
        <GrStatusBadge :status="row.item.status" :syncDt="row.item.syncDt" />
      </template>

      <template v-slot:cell(receivedDt)="row">
        {{ formatDate(row.item.receivedDt) }}
      </template>

      <template v-slot:cell(createdAt)="row">
        {{ formatDate(row.item.createdAt) }}
      </template>

      <template v-slot:cell(buttons)="row">
        <div class="action-icons">
          <span
            class="icon-tooltip-wrapper"
            data-tooltip="ดูข้อมูลใบรับสินค้า"
          >
            <i
              class="fas fa-info-circle action-icon info-icon"
              @click="$emit('info', row.item)"
            ></i>
          </span>
          <span
            v-if="showEdit"
            :class="{
              'icon-tooltip-wrapper': isGrEditable(row.item),
            }"
            data-tooltip="แก้ไขใบรับสินค้า"
          >
            <i
              class="fas fa-edit action-icon edit-icon"
              :class="{ 'icon-disabled': !isGrEditable(row.item) }"
              @click="isGrEditable(row.item) && $emit('edit', row.item)"
            ></i>
          </span>
          <span
            v-if="showDelete"
            :class="{
              'icon-tooltip-wrapper': isGrEditable(row.item),
            }"
            data-tooltip="ยกเลิกใบรับสินค้า"
          >
            <i
              class="fas fa-trash-alt action-icon delete-icon icon-delete-hover"
              :class="{ 'icon-disabled': !isGrEditable(row.item) }"
              @click="isGrEditable(row.item) && $emit('delete', row.item)"
            ></i>
          </span>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { THAI_MONTHS } from "@/constants";
import GrStatusBadge from "@/components/badge/GrStatusBadge.vue";
import { GR_STATUS } from "@/constants/productStock";

export default {
  name: "GrListTab",
  components: {
    GrStatusBadge,
  },
  props: {
    /** Unique HTML id for the b-table (required to avoid duplicate id warnings) */
    tableId: {
      type: String,
      required: true,
    },
    /** Array of GR items to display */
    items: {
      type: Array,
      default: () => [],
    },
    /** Current search filter text (supports .sync via update:filter event) */
    filter: {
      type: String,
      default: "",
    },
    /** Whether to show the edit button */
    showEdit: {
      type: Boolean,
      default: true,
    },
    /** Whether to show the delete button */
    showDelete: {
      type: Boolean,
      default: true,
    },
    /** Whether to show the add button */
    showAddButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:filter", "add-gr", "edit", "info", "delete"],
  data() {
    return {
      grFields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "4%" },
          tdClass: "index-cell",
        },
        {
          key: "status",
          label: "สถานะ",
          sortable: true,
          sortByFormatted: true,
          formatter: value => {
            const order = {
              [GR_STATUS.WAITING_BM]: 0,
              [GR_STATUS.WAITING_HQ]: 1,
              [GR_STATUS.REJECTED]: 2,
              [GR_STATUS.COMPLETED]: 3,
              [GR_STATUS.CANCELED]: 4,
            };
            return order[value] ?? 99;
          },
          thStyle: { width: "14%", whiteSpace: "nowrap" },
        },
        {
          key: "grCode",
          label: "รหัสใบรับสินค้า",
          sortable: true,
          thStyle: { width: "18%", whiteSpace: "nowrap" },
        },
        {
          key: "poRefCode",
          label: "รหัสใบสั่งซื้ออ้างอิง",
          sortable: true,
          thStyle: { width: "18%", whiteSpace: "nowrap" },
        },
        {
          key: "vendorName",
          label: "ชื่อผู้ขาย",
          sortable: true,
          thStyle: { width: "20%", whiteSpace: "nowrap" },
          tdClass: "vendor-name-cell",
        },
        {
          key: "receivedDt",
          label: "วันที่รับของ",
          sortable: true,
          thStyle: { width: "14%", whiteSpace: "nowrap" },
        },
        {
          key: "createdAt",
          label: "วันที่ทำรายการ",
          sortable: true,
          sortDirection: "desc",
          thStyle: { width: "14%", whiteSpace: "nowrap" },
        },
        {
          key: "buttons",
          label: "",
          thStyle: { width: "12%" },
          class: "text-center",
        },
      ],
    };
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = THAI_MONTHS[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day} ${month} ${year}`;
    },
    isGrEditable(item) {
      return !!item && item.syncDt === null && (item.status === GR_STATUS.WAITING_BM || item.status === GR_STATUS.REJECTED);
    },
  },
};
</script>

<style scoped>
.add-gr-btn {
  border: 1px solid #1976d2;
  background-color: #1976d2;
  color: #fff;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.add-gr-btn:hover {
  background-color: #1565c0;
  color: #fff;
}

/* Table styles */
::v-deep table {
  table-layout: auto;
}

::v-deep table thead th {
  padding: 16px 24px;
}

::v-deep table tbody td {
  padding: 16px 24px;
  vertical-align: middle;
}

::v-deep .index-cell {
  white-space: nowrap;
}

::v-deep .vendor-name-cell {
  word-break: break-word;
  white-space: normal;
}

/* Action icons */
.action-icons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.icon-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-tooltip-wrapper::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background-color: #212529;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 8px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 10000;
}

.icon-tooltip-wrapper::before {
  content: "";
  position: absolute;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #212529;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 10001;
}

.icon-tooltip-wrapper:hover::after,
.icon-tooltip-wrapper:hover::before {
  opacity: 1;
}

.action-icon {
  font-size: 20px;
  color: #adb5bd;
  cursor: pointer;
}

.icon-delete-hover:hover {
  color: #d33 !important;
}

.action-icon:hover:not(.icon-disabled) {
  color: #1976d2;
}

.info-icon {
  font-size: 20px;
}

.icon-disabled {
  color: #dee2e6;
  cursor: not-allowed;
  pointer-events: none;
}

@media (max-width: 576px) {
  ::v-deep table thead th,
  ::v-deep table tbody td {
    padding: 10px 12px;
  }

  .action-icons {
    gap: 10px;
  }

  .action-icon {
    font-size: 20px;
  }
}
</style>
